import React, { useContext, useEffect, useState } from "react";
import FarmerProfileInfoCard from "./ProfileInfoCard";
import { View, Text, StyleSheet } from "react-native";
import {
  BG_STYLES,
  BORDER_STYLES,
  Button,
  BUTTON_TYPE,
  COMMON_STYLES,
  SIZE,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import { ResponsiveContext } from "app/context/ResponsiveUIContext";
import fetchFarmerDetails from "../../apis/fetchFarmerDetails";
import { FarmerDetails } from "../../models/farmer";
import EditIcon from "../../icons/EditIcon";
import { rnNavigation } from "app/utils/appConfig";
import { ROUTES } from "../../routes/types";
import { isMobileDevice } from "app/utils/common";

interface Props {
  farmerId: string;
  isEditAllowed: boolean;
  highlight: boolean;
  onAuthReady?: () => void;
}

const FarmerProfileSection = ({
  farmerId,
  isEditAllowed,
  highlight,
  onAuthReady,
}: Props) => {
  const { mediaStyles } = useContext(ResponsiveContext);
  const [loading, setLoading] = useState(true);
  const [farmerData, setFarmerData] = useState<FarmerDetails>();

  const isDesktop = !isMobileDevice();

  const fetchAndSetFarmerDetails = async (id: string) => {
    const details = await fetchFarmerDetails(id);
    if (details) {
      document.cookie = `userAuthId=${details.auth_id}; path=/`;
      setFarmerData(details);
      onAuthReady?.();
    }
  };

  useEffect(() => {
    fetchAndSetFarmerDetails(farmerId).finally(() => setLoading(false));
  }, [farmerId]);

  return (
    <View
      style={[
        COMMON_STYLES["ph3/2"],
        COMMON_STYLES.pv1,
        BG_STYLES.bgBlue10,
        COMMON_STYLES.mv1,
        mediaStyles({
          desktopStyle: [styles["br5/4"], styles.minHeight],
        }),
        highlight && [COMMON_STYLES.b1, BORDER_STYLES.borderSuccess100],
      ]}
    >
      <View style={[COMMON_STYLES.row, COMMON_STYLES.justifyBetween]}>
        <Text
          style={[
            TYPOGRAPHY.HEADING2,
            VARIANTS_STYLES.MEDIUM,
            TEXT_STYLES.textNeutral100,
            COMMON_STYLES.mb2,
          ]}
        >
          Personal Information
        </Text>

        {isEditAllowed && (
          <Button
            onClick={() =>
              rnNavigation({
                name: ROUTES.EDIT_FARMER_DETAILS,
                params: { farmerId },
                replace: true,
              })
            }
            text={isDesktop ? "Edit Details" : "Edit"}
            type={isDesktop ? BUTTON_TYPE.SECONDARY : BUTTON_TYPE.TERTIARY}
            buttonStyle={[
              mediaStyles({
                desktopStyle: [
                  BG_STYLES.bgWhite,
                  BORDER_STYLES.borderNeutral10,
                ],
                mobileStyle: [COMMON_STYLES.p0, COMMON_STYLES.alignItemsStart],
              }),
              styles["h5/2"],
              COMMON_STYLES["ml0.5"],
            ]}
            textStyle={[TYPOGRAPHY.HEADING3, VARIANTS_STYLES.MEDIUM]}
            iconLeft={isDesktop && <EditIcon />}
            size={SIZE.small}
          />
        )}
      </View>

      <FarmerProfileInfoCard
        loading={loading}
        name={farmerData?.full_name}
        gender={farmerData?.profile.gender}
        phone={farmerData?.phone_number}
        age={farmerData?.profile.display_age || ""}
        address={farmerData?.full_address}
        land={
          farmerData?.profile.total_leased_land
            ? `${farmerData.profile.total_leased_land} ${
                farmerData.profile.total_leased_land_unit || "Acre"
              }`
            : ""
        }
        livestock={farmerData?.livestock_display}
        transactions={farmerData?.last_one_yr_order_total}
        smartPhpneUser={
          farmerData?.profile.smartphone_user === null
            ? "--"
            : farmerData?.profile.smartphone_user
              ? "True"
              : "False"
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  "flex1/3": { flex: 1 / 3 },
  "gap7/4": { gap: 28 },
  "br5/4": { borderRadius: 24 },
  w30: { width: "30%" },
  minHeight: { minHeight: 264 },
  "h5/2": { height: 42 },
});

export default FarmerProfileSection;
